import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { useUsers } from '../hooks/useUsers';
import type { User } from '../types';

interface UserContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  isRegistered: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { address, isConnected } = useAccount();
  const { currentUser, setCurrentUserByWallet, clearCurrentUser, loading, error } = useUsers();
  
  const [isRegistered, setIsRegistered] = useState(false);

  // Re-enabled user fetching with proper error handling
  useEffect(() => {
    if (isConnected && address) {
      console.log('Fetching user for address:', address);
      setCurrentUserByWallet(address).catch((error) => {
        console.warn('User not found in database:', error);
        clearCurrentUser(); // Set to null if user not found
      });
    } else {
      clearCurrentUser();
      setIsRegistered(false);
    }
  }, [isConnected, address, setCurrentUserByWallet, clearCurrentUser]);

  // Update registration status when currentUser changes
  useEffect(() => {
    setIsRegistered(!!currentUser);
  }, [currentUser]);

  const refreshUser = async () => {
    if (address) {
      await setCurrentUserByWallet(address);
    }
  };

  const value: UserContextType = {
    currentUser,
    loading,
    error,
    isRegistered,
    refreshUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
